// Provider byte and page ceilings, checked early because the document is merged and stored before
// dispatch: refusing up front tells the sender what is wrong, while a far-end refusal leaves an
// unactionable failed row.

/** The provider's hard limit; larger submissions are refused at their end. */
export const FAX_MAX_TOTAL_BYTES = 50 * 1024 * 1024;
/** The provider's hard limit. */
export const FAX_MAX_PAGES = 350;
/** This application's own limit on how many files make up one fax. */
export const FAX_MAX_FILES = 10;
/** And on how many chart documents may be pulled into one. */
export const FAX_MAX_CHART_DOCS = 10;

/** What goes on the front: `text` builds a cover sheet from subject and note, `upload` uses the
 * sender's PDF, `none` sends documents alone. */
export const FAX_COVER_MODES = ["none", "text", "upload"] as const;
export type FaxCoverMode = (typeof FAX_COVER_MODES)[number];

/** Chart documents pullable into a fax: rendered on demand (always offered), signed uploaded
 * documents (unavailable until the signature returns), and other chart documents (`document`). */
export const FAX_RESIDENT_DOC_KINDS = [
  "face_sheet",
  "admission_packet",
  "therapy_packet",
  "therapy_consent",
  "therapy_referral",
  "therapy_note",
  "care_plan",
  "physician_poc",
  "physician_al_order",
  "mar",
  "physician_order_sheet",
  "invoice",
  "document",
] as const;
export type FaxResidentDocKind = (typeof FAX_RESIDENT_DOC_KINDS)[number];

/** Documents that come in quantity are collapsed under a heading by the picker. */
export type FaxResidentDocGroup = "invoices" | "documents";

export interface FaxResidentDocOption {
  kind: FaxResidentDocKind;
  /** Which one, when the kind has more than one: an invoice id, a plan id. */
  id: string | null;
  label: string;
  available: boolean;
  detail?: string;
  group?: FaxResidentDocGroup;
}

/** "2026-08" as "August 2026". The month is range-checked because `Date` rolls a thirteenth month
 * into January and would print a confident, wrong label. */
export function billingPeriodLabel(period: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return period;
  const date = new Date(Number(match[1]), month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** How big something is, in the words a refusal should use. */
export function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type FaxSizeRefusal = { ok: false; reason: string } | { ok: true };

/** Whether a prepared fax is within what the provider will take; separate from sending so edges
 * test without a provider. */
export function faxWithinLimits(input: { bytes: number; pages: number }): FaxSizeRefusal {
  if (input.pages > FAX_MAX_PAGES) {
    return {
      ok: false,
      reason: `The fax is ${input.pages} pages and the limit is ${FAX_MAX_PAGES}.`,
    };
  }
  if (input.bytes > FAX_MAX_TOTAL_BYTES) {
    return {
      ok: false,
      reason: `The documents come to ${megabytes(input.bytes)} and the fax limit is ${megabytes(
        FAX_MAX_TOTAL_BYTES,
      )}.`,
    };
  }
  return { ok: true };
}

/** Whether bytes begin the way a PDF does. Faxing anything else is refused. */
export function looksLikePdfBytes(bytes: Uint8Array): boolean {
  if (bytes.length < 5) return false;
  return (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}

/** Columns the fax log may be ordered on: newest first by default, or by the column someone is
 * chasing a fax through. */
export const FAX_SORT_KEYS = [
  "date",
  "facility",
  "status",
  "pages",
  "from",
  "to",
  "direction",
] as const;
export type FaxSortKey = (typeof FAX_SORT_KEYS)[number];

/** Which column the address book is keyed on: the same people are reached by fax or email, so one
 * set of rows answers both. */
export const FAX_CONTACT_CHANNELS = ["fax", "email", "phone"] as const;
export type FaxContactChannel = (typeof FAX_CONTACT_CHANNELS)[number];

/** Where a contact-book entry came from: saved by hand, or the care network. */
export const FAX_CONTACT_SOURCES = ["contact", "provider", "pharmacy", "institution"] as const;
export type FaxContactSource = (typeof FAX_CONTACT_SOURCES)[number];
